/**
 * usePolygon Hook
 * Manages polygon selection and plotting
 * Uses PolygonService for operations
 * Subscribes to store for selectedPolygon
 *
 * Key resilience features:
 * - Polls for map readiness instead of just checking once (handles fast clicks).
 * - Cancels in-flight plots when a new polygon is selected.
 * - Re-checks map/polygon identity before fitBounds to avoid stale operations.
 */
import { useEffect, useRef } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { polygonService } from '@/services/map/PolygonService';
import { useMapStore } from '@/stores/slices/useMapStore';
import { nowTimestamp } from '@/utils/date';

/** Max time (ms) to wait for map to be ready */
const MAP_READY_TIMEOUT = 10000;
/** Polling interval (ms) to check if map is ready */
const MAP_READY_POLL_INTERVAL = 100;

/**
 * Hook for managing polygon plotting
 * Automatically plots polygon when selectedPolygon changes in store
 */
export function usePolygon(): void {
  const { map, isMapReady } = useMapbox();
  const selectedPolygon = useMapStore((state) => state.selectedPolygon);

  // Track the WKT of the polygon we're currently trying to plot
  const targetWktRef = useRef<string | null>(null);

  useEffect(() => {
    // Capture the WKT we intend to plot in this effect run
    const wkt = selectedPolygon?.polygon ?? null;
    targetWktRef.current = wkt;

    // Cleanup helpers
    let pollIntervalId: ReturnType<typeof setInterval> | null = null;
    const timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      if (pollIntervalId) clearInterval(pollIntervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    // If polygon was cleared, remove from map
    if (!wkt) {
      if (map.current && isMapReady) {
        try {
          if (map.current.isStyleLoaded()) {
            polygonService.removePolygon();
          }
        } catch {
          // ignore
        }
      }
      return cleanup;
    }

    /**
     * Actually plot the polygon.
     * Guards against stale calls by checking `cancelled` and `targetWktRef`.
     */
    const executePlot = async () => {
      // Abort if effect was cleaned up or a newer polygon was selected
      if (cancelled || targetWktRef.current !== wkt) {
        return;
      }

      const mapInstance = map.current;
      if (!mapInstance || !mapInstance.isStyleLoaded()) {
        return false; // Not ready yet
      }

      try {
        const success = await polygonService.plotPolygon(wkt, {
          fitBounds: true,
          onComplete: () => {
            // Final guard: only log if this is still the active polygon
            if (targetWktRef.current === wkt) {
              // eslint-disable-next-line no-console
              // Polygon plotted and map centered.
            }
          },
        });

        if (!success && targetWktRef.current === wkt) {
          console.warn('Polygon: plotPolygon returned false');
        }
        return success;
      } catch (err) {
        if (targetWktRef.current === wkt) {
          console.error('Polygon: error plotting', err);
        }
        return false;
      }
    };

    /**
     * Try to plot immediately if map is ready, otherwise poll until ready
     */
    const tryPlot = async () => {
      // If map and style are ready, plot immediately
      if (map.current && isMapReady && map.current.isStyleLoaded()) {
        const success = await executePlot();
        if (success) {
          cleanup();
          return;
        }
      }

      // Otherwise, poll until map is ready or timeout
      const startTime = nowTimestamp();

      pollIntervalId = setInterval(async () => {
        if (cancelled || targetWktRef.current !== wkt) {
          cleanup();
          return;
        }

        // Check if timed out
        if (nowTimestamp() - startTime > MAP_READY_TIMEOUT) {
          console.warn('Polygon: timed out waiting for map to be ready');
          cleanup();
          return;
        }

        // Try to plot if map is now ready
        if (map.current?.isStyleLoaded()) {
          const success = await executePlot();
          if (success) {
            cleanup();
          }
        }
      }, MAP_READY_POLL_INTERVAL);
    };

    void tryPlot();

    return cleanup;
  }, [selectedPolygon, isMapReady, map]);
}
